/** biome-ignore-all lint/style/noExcessiveLinesPerFile: TODO: shorten this file on CD */
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { isBoolean, isEmpty } from "lodash";
import {
  BarChart2,
  Briefcase,
  Building2,
  Download,
  ExternalLink,
  Eye,
  Link as LinkIcon,
  Tag,
} from "lucide-react";
import { type ReactElement, type ReactNode, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Element } from "react-scroll";

import {
  RepaymentCapacityTab,
  SectionNav,
  type SectionNavItem,
} from "@krea/lender-features";
import { BankingDataChart } from "@krea/lender-features/components/banking-data-chart";
import { config } from "@krea/shared/config";
import { Alert } from "@krea/shared/design-system/alerts/Alert";
import { Button } from "@krea/shared/design-system/Button";
import FileSection from "@krea/shared/design-system/file-section";
import FileSectionAction from "@krea/shared/design-system/file-section/FileSectionAction";
import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";
import { Spinner } from "@krea/shared/design-system/Spinner";
import { Table } from "@krea/shared/design-system/table";
import { toast } from "@krea/shared/design-system/utils/toast";
import { useAuthStore } from "@krea/shared/features/auth/authStore";
import { Currency, type EnvironmentEnum } from "@krea/shared/types/common";
import { cn } from "@krea/shared/utils";
import { downloadFile } from "@krea/shared/utils/downloadFile";
import { getAppUrls } from "@krea/shared/utils/get-app-urls";

import { CaseAssistant } from "@/features/case-assistant";
import {
  BidTypeEnum,
  LenderApplicationTypeEnum,
} from "@/features/common/enums";
import { useGetLenderApplicationDetailQuery } from "@/features/lender-applications/hooks/data/use-get-lender-application-detail.query";
import type { ILenderApplicationExtended } from "@/features/lender-applications/types/lender-application";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import type {
  FieldRequestObject,
  GroupedFieldRequestSubmittedDataChoice,
  IFieldRequest,
} from "@/features/lender-responses/types/field-request";
import type { ILenderResponse } from "@/features/lender-responses/types/lender-response";
import { useGetMeQuery } from "@/features/users/hooks/use-get-me.query";
import { bidValidationSchema } from "@/validations";

import { useGetAccountingData } from "../../store/accounting-data";
import { formatTime, ScrollToElement, timeTo, ucFirst } from "../../utils";
import { useFormatCurrency } from "../../utils/customHooks/useFormatCurrency";
import { useMobile } from "../../utils/customHooks/useMobile";
import DetailsCard from "../details/card";
import { DetailsContainer } from "../details/container";
import { DetailsItemGridData } from "../details/item-grid/data";
import { DetailsItemGrid } from "../details/item-grid/grid";
import { DetailsItemList } from "../details/item-list/index";
import { HighlightedCustomerSegmentIndicator } from "../highlighted-customer-segment-indicator";
import { StatusLabel } from "../status-label";
import { AttachedFile } from "./attached-file";
import Bids from "./bids";
import { FormSwitcher } from "./bids/FormSwitcher";
import { LenderResponse } from "./bids/LenderResponse";
import { CreditCaseFileBlock } from "./blocks/CreditCaseFileBlock";
import { CompanyStructure } from "./company-structure";
import { LenderNotes } from "./lender-notes";
import { useLenderApplicationDetailsStore } from "./lenderApplicationDetailsStore";
import Toolbar from "./toolbar";

const ToolbarTime = ({
  title,
  time,
  timeWarning,
  className,
}: {
  title?: string;
  time?: ReactNode;
  timeWarning?: boolean;
  className?: string;
}) => {
  return (
    <dl className={cn("flex items-center mx-0 my-1", className)}>
      <dt className="m-0 text-base">{`${title}:`}</dt>
      <dd
        className={cn("m-0 ml-1 text-base", {
          "text-red-500": timeWarning,
        })}
      >
        {time}
      </dd>
    </dl>
  );
};

const AttachedLink = ({ value }: { value: string }) => {
  let href: string;

  try {
    href = new URL(value).href;
  } catch (e: any) {
    if (e.message.includes(" Invalid URL")) href = `//${value}`;
    else href = "";
  }

  const linkContent = (
    <div className="my-1 flex items-center overflow-hidden pr-3">
      <LinkIcon className="size-4" />
      <a
        className="block overflow-hidden text-ellipsis whitespace-nowrap"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ maxWidth: "400px" }}
      >
        {value}
      </a>
    </div>
  );

  return (
    <div className="flex flex-wrap items-center">
      <ResponsiveTooltip trigger={linkContent} hidden={value.length <= 46}>
        {value}
      </ResponsiveTooltip>
    </div>
  );
};

const TextItem = ({
  text,
  notExists,
  value,
  className,
}: {
  text: string;
  notExists?: boolean;
  value?: string | ReactElement;
  className?: string;
}) => {
  const currentValue = value ? value : "";

  return (
    <div
      className={cn("flex flex-col gap-1", className, {
        "opacity-50": notExists,
      })}
    >
      <span className="text-label">{text}</span>
      <span className="text-base-strong">
        {notExists ? "No information" : currentValue}
      </span>
    </div>
  );
};

const FreeText = ({ title, text }: { title?: string; text?: string }) => {
  return (
    <div className="mb-2 flex flex-wrap">
      {title && <div className="mb-2 shrink-0 pr-2 text-label">{title}</div>}

      {text && <div className="whitespace-pre-wrap">{text}</div>}
    </div>
  );
};

interface ISelectedBidComponentProps {
  lenderResponse: ILenderResponse;
  lenderApplication: ILenderApplicationExtended;
}

const SelectedBidComponent = ({
  lenderResponse,
  lenderApplication,
}: ISelectedBidComponentProps) => {
  return (
    <LenderResponse
      lenderResponse={lenderResponse}
      lenderApplication={lenderApplication}
      preview={false}
    />
  );
};

const LenderApplicationDetails = () => {
  const _navigate = useNavigate();
  const isMobile = useMobile();

  const { lenderCompanyId, id } = useParams();
  const {
    data: lenderApplication,
    isPending,
    isError,
  } = useGetLenderApplicationDetailQuery(Number(lenderCompanyId), Number(id));

  const { data: accountingData } = useGetAccountingData(
    Number(lenderApplication?.loanApplication.id),
    {
      enabled: !!lenderApplication?.loanApplication.id,
    },
  );
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const { data: me } = useGetMeQuery();

  const countryCode = lenderCompany?.countryCode;
  const setCurrentlyUpdatingLenderResponseId = useLenderApplicationDetailsStore(
    (state) => state.setCurrentlyUpdatingLenderResponseId,
  );

  const currency = lenderApplication?.loanApplication.currency ?? Currency.SEK;
  const [formattedCurrencyValue] = useFormatCurrency({
    currency,
  });

  const methods = useForm({
    mode: "all",
    criteriaMode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(
      bidValidationSchema({
        currency,
        estimatedInvoiceAmount: 0,
        formatWithCurrency: (value) => formattedCurrencyValue(value, currency),
      }),
    ),
    defaultValues: {
      bids: [],
      fieldRequests: [],
      promotions: [],
      creditTermsFile: undefined,
    },
    shouldUnregister: false,
  });

  const fieldRequestObject = useMemo(() => {
    return lenderApplication?.loanApplication.fieldRequests.reduce(
      (finalObj, request) => {
        // prepare an object which will have the fieldRequest field name as key and the whole fieldRequest as value
        finalObj[request.field] = request as never;

        return finalObj;
      },
      {} as FieldRequestObject,
    );
  }, [lenderApplication?.loanApplication.fieldRequests]);

  const realEstateRepaymentChoicesObject = useMemo(() => {
    const groupedRepaymentFR =
      fieldRequestObject?.grouped_real_estate_repayment as
        | IFieldRequest<{
            choices: GroupedFieldRequestSubmittedDataChoice[];
          }>
        | undefined;

    const choices = groupedRepaymentFR?.data?.choices;

    if (!choices) {
      return {};
    }

    interface IChoice {
      name: string;
      value: string;
      selected: boolean;
    }

    return choices.reduce<Record<string, IChoice | undefined>>(
      (finalObj, repaymentChoice: IChoice) => {
        finalObj[repaymentChoice.value.toLowerCase()] = repaymentChoice;

        return finalObj;
      },
      {},
    );
  }, [fieldRequestObject?.grouped_real_estate_repayment]);

  if (isPending || isError)
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner className="text-primary" size="xl" />
      </div>
    );

  const selectedLenderResponse =
    lenderApplication.loanApplication.selectedLenderResponse;
  const selectedLenderResponseId = selectedLenderResponse?.id;
  const loanApplication = lenderApplication.loanApplication;
  const lenderApplicationId = lenderApplication.id;
  const contactPerson = loanApplication.contactPerson;
  const applyingCompany = loanApplication.applyingCompany;
  const latestFinancialRecord = applyingCompany.latestFinancialRecord;
  const insights = loanApplication.insights;

  const customerCompany = applyingCompany.customerCompany;

  const companyName = customerCompany.name;
  const companyOrgNumber = customerCompany.organisationNumber;

  const refreshToken = useAuthStore.getState().refreshToken;
  const isOwnApplication = me?.ssn === loanApplication.contactPerson?.ssn;
  const customerAppUrl =
    isOwnApplication && countryCode && refreshToken
      ? `${getAppUrls(loanApplication.countryCode, config.environment as EnvironmentEnum).customer}/current-application/${loanApplication.id}?quickAuth=${refreshToken}`
      : null;

  const dateReceived = formatTime(lenderApplication.markedEligibleForLenderAt);
  const dateUpdated = formatTime(loanApplication.updatedAt);
  const timeToResult = timeTo(loanApplication.biddingEndsAt);
  const dateBiddingEnds = timeToResult.restTime;
  const timeWarning = timeToResult.warning;

  const lenderResponsesWithLoanBids = lenderApplication.lenderResponses?.filter(
    ({ id, bids }) =>
      bids.length === 1 &&
      bids[0]?.type === BidTypeEnum.COMPANY_LOAN &&
      id !== selectedLenderResponseId,
  );

  const lenderResponsesWithFactoringBids =
    lenderApplication.lenderResponses?.filter(
      ({ id, bids }) =>
        bids.length === 1 &&
        bids[0]?.type === BidTypeEnum.FACTORING &&
        id !== selectedLenderResponseId,
    );

  const lenderResponsesWithRealEstateBids =
    lenderApplication.lenderResponses?.filter(
      ({ id, bids }) =>
        bids.length === 1 &&
        bids[0]?.type === BidTypeEnum.REAL_ESTATE &&
        id !== selectedLenderResponseId,
    );

  const lenderResponsesWithLeasingBids =
    lenderApplication.lenderResponses?.filter(
      ({ id, bids }) =>
        bids.length === 1 &&
        bids[0]?.type === BidTypeEnum.LEASING &&
        id !== selectedLenderResponseId,
    );

  const lenderResponsesWithRevolvingCreditBids =
    lenderApplication.lenderResponses?.filter(
      ({ id, bids }) =>
        bids.length === 1 &&
        bids[0]?.type === BidTypeEnum.REVOLVING_CREDIT &&
        id !== selectedLenderResponseId,
    );

  const lenderResponsesWithPurchaseFinancingBids =
    lenderApplication.lenderResponses?.filter(
      ({ id, bids }) =>
        bids.length === 1 &&
        bids[0]?.type === BidTypeEnum.PURCHASE_FINANCING &&
        id !== selectedLenderResponseId,
    );

  const lenderResponsesWithBuildingCreditBids =
    lenderApplication.lenderResponses?.filter(
      ({ id, bids }) =>
        bids.length === 1 &&
        bids[0]?.type === BidTypeEnum.BUILDING_CREDIT &&
        id !== selectedLenderResponseId,
    );

  const lenderResponsesWithMultipleBids =
    lenderApplication.lenderResponses?.filter(
      ({ id, bids }) => bids.length > 1 && id !== selectedLenderResponseId,
    );

  const placeAnotherBid = () => {
    setCurrentlyUpdatingLenderResponseId(null);
    ScrollToElement("scrollToBidsForm");
  };

  const reasonText =
    loanApplication.reasonDisplayName !== loanApplication.reason
      ? `${loanApplication.reasonDisplayName} (${loanApplication.reason})`
      : loanApplication.reasonDisplayName;

  const signatoriesText = loanApplication.contactPersonHasSigningRightForCompany
    ? "Contact person has signing rights"
    : "Contact person does not have signing rights";

  const beneficialOwnerText = applyingCompany.beneficialOwnerExists
    ? "Beneficial Owner is registered"
    : "Beneficial Owner not registered";

  const groupStructureText = applyingCompany.groupStructureExists
    ? "Group structure exists"
    : "Group structure does not exist";

  const getIsPepTextByCondition = (isPep?: boolean | null) => {
    switch (isPep) {
      case true:
        return "Registered as part of PEP register";
      case false:
        return "Not registered as part of PEP register";
      default:
        return "No information";
    }
  };

  const hasCreditCaseFile = Boolean(applyingCompany.creditCaseFile?.fileUrl);

  const applicationSections: SectionNavItem[] = [
    { id: "section-company", label: "Company", icon: Building2 },
    ...(hasCreditCaseFile
      ? [
          {
            id: "section-financing",
            label: "Financing - business case",
            icon: Briefcase,
          },
        ]
      : []),
    {
      id: "section-financial",
      label: "Financial information",
      icon: BarChart2,
      children: [
        { id: "revenue-overview", label: "Revenue & risk" },
        { id: "financial-year", label: "Annual Financials" },
        { id: "key-ratios", label: "Financial Ratios" },
      ],
    },
    { id: "section-offer", label: "Offer", icon: Tag },
  ];

  return (
    <FormProvider {...methods}>
      {lenderCompanyId && (
        <CaseAssistant
          key={String(lenderApplicationId)}
          lenderCompanyId={lenderCompanyId}
          lenderApplicationId={String(lenderApplicationId)}
          countryCode={loanApplication.countryCode as string}
          companyName={companyName}
          orgNumber={companyOrgNumber}
        />
      )}
      <div className="pb-10">
        <div className="mb-4 flex-wrap items-start border-b pb-3 md:items-center md:justify-between">
          <div className="-mx-2 mt-4 flex flex-wrap md:mt-0">
            <div className="shrink-0 px-2">
              <ToolbarTime title="Received" time={dateReceived} />
            </div>

            {dayjs(dateUpdated).isAfter(dayjs(dateReceived)) && (
              <div className="shrink-0 px-2">
                <ToolbarTime title="Updated" time={dateUpdated} />
              </div>
            )}

            <div className="shrink-0 px-2">
              <ToolbarTime
                title="Time left"
                time={dateBiddingEnds}
                timeWarning={timeWarning}
              />
            </div>
          </div>
        </div>

        <Toolbar lenderApplication={lenderApplication} />

        <div className="grid lg:grid-cols-[220px_1fr] gap-8">
          {/* Left column — sticky section nav */}
          <div className="hidden lg:block">
            <SectionNav
              sections={applicationSections}
              scrollContainerSelector="#main-content-scroll-container"
              offsetTopPx={12}
            />
          </div>
          <div className="min-w-0">
            <DetailsContainer
              title={
                <div className="flex items-center">
                  <span>{`${companyName} (${companyOrgNumber})`}</span>
                  {lenderApplication.isHighlightedCustomerSegment && (
                    <HighlightedCustomerSegmentIndicator className={"ml-2"} />
                  )}
                  {customerAppUrl && !config.isProduction && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex items-center gap-1 ml-2"
                      onClick={() =>
                        window.open(
                          customerAppUrl,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      <ExternalLink className="size-4" />
                      <span>View as customer</span>
                    </Button>
                  )}
                </div>
              }
              status={
                <StatusLabel
                  type={lenderApplication.type}
                  hasActiveLenderResponses={Boolean(
                    lenderApplication.lenderResponses &&
                      lenderApplication.lenderResponses.filter(
                        (response) => response.isActive,
                      ).length > 0,
                  )}
                />
              }
            >
              {[
                LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_BID_SELECTED,
                LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_PAYOUT_RECEIVED_BY_CUSTOMER,
              ].includes(lenderApplication.type) ? (
                <>
                  {selectedLenderResponse && (
                    <div className="-mx-3 flex flex-wrap">
                      <div className="mb-10 mt-5 w-full px-3">
                        <DetailsContainer title="Selected offer">
                          <SelectedBidComponent
                            lenderResponse={selectedLenderResponse}
                            lenderApplication={lenderApplication}
                          />
                        </DetailsContainer>
                      </div>
                    </div>
                  )}
                  <div className="-mx-3 flex flex-wrap">
                    <div className="w-full px-3">
                      {lenderApplication.type ===
                      LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_BID_SELECTED ? (
                        <Element name="scrollToNotes">
                          <LenderNotes
                            lenderInternalNotes={
                              lenderApplication.lenderInternalComments
                            }
                            lenderInternalCommentsUpdatedAt={
                              lenderApplication.lenderInternalCommentsUpdatedAt
                            }
                            lenderApplicationId={lenderApplicationId}
                          />
                        </Element>
                      ) : null}
                    </div>
                  </div>
                </>
              ) : null}
              <div
                id="section-loan-details"
                style={{ scrollMarginTop: 80 }}
                className="-mx-3 flex flex-wrap"
              >
                <div
                  id="section-company"
                  style={{ scrollMarginTop: 80 }}
                  className="mb-5 w-full px-3 xl:w-1/2"
                >
                  <DetailsCard title="About company">
                    <DetailsItemGrid>
                      <DetailsItemGridData
                        title="Company"
                        value={`${companyName} (${companyOrgNumber})`}
                      />

                      {customerCompany.type && (
                        <DetailsItemGridData
                          title="Company type"
                          value={ucFirst(
                            customerCompany.type
                              .replace(/[_]+/g, " ")
                              .toLowerCase(),
                          )}
                        />
                      )}

                      {applyingCompany.sniCodes &&
                        applyingCompany.sniCodes.length > 0 && (
                          <DetailsItemGridData
                            title="Industry"
                            value={
                              <DetailsItemList>
                                {applyingCompany.sniCodes.map(
                                  (
                                    item: Record<string, string>,
                                    index: number,
                                  ) => (
                                    <li
                                      key={index}
                                    >{`${item.code} - ${item.description}`}</li>
                                  ),
                                )}
                              </DetailsItemList>
                            }
                          />
                        )}

                      {customerCompany.numberEmployeesInterval && (
                        <DetailsItemGridData
                          title="Employees"
                          value={customerCompany.numberEmployeesInterval}
                        />
                      )}

                      {customerCompany.county && (
                        <DetailsItemGridData
                          title="County"
                          value={customerCompany.county}
                        />
                      )}

                      {customerCompany.incorporationDate && (
                        <DetailsItemGridData
                          title="Incorporation"
                          value={customerCompany.incorporationDate}
                        />
                      )}

                      <DetailsItemGridData
                        indicator
                        title="Group structure"
                        value={groupStructureText}
                        isDone={!applyingCompany.groupStructureExists}
                        notExists={applyingCompany.groupStructureExists == null}
                        tooltip={groupStructureText}
                      />
                    </DetailsItemGrid>
                  </DetailsCard>
                </div>
                <div className="mb-5 w-full px-3 xl:w-1/2">
                  <DetailsCard title="Contact person">
                    {contactPerson.isPep && (
                      <Alert variant="warning" className="px-3 py-2">
                        Politically exposed person
                      </Alert>
                    )}

                    <DetailsItemGrid>
                      <DetailsItemGridData
                        title="First name"
                        value={contactPerson.firstName}
                      />

                      <DetailsItemGridData
                        title="Last name"
                        value={contactPerson.lastName}
                      />

                      {contactPerson.ssn && (
                        <DetailsItemGridData
                          title="SSN"
                          value={contactPerson.ssn}
                        />
                      )}

                      {/*
                TODO : Turned off for now, See KREA-2699
                <DetailsItemGridData
                  title="Background check"
                  indicator={getPersonBackground(contactPerson.ssn) !== null}
                  isDone={getPersonBackground(contactPerson.ssn) === 'OK'}
                  value={personBackgroundText(contactPerson.ssn)}
                  notExists={getPersonBackground(contactPerson.ssn) === null}
                  tooltip={personBackgroundText(contactPerson.ssn)}
                /> */}

                      {contactPerson.mobilePhoneNumber && (
                        <DetailsItemGridData
                          title="Mobile phone"
                          value={contactPerson.mobilePhoneNumber}
                        />
                      )}

                      {contactPerson.contactEmail && (
                        <DetailsItemGridData
                          title="Email"
                          value={contactPerson.contactEmail}
                        />
                      )}
                    </DetailsItemGrid>
                  </DetailsCard>
                </div>

                {applyingCompany.groupStructure && (
                  <div className="mb-5 w-full px-3">
                    <DetailsCard title="Group structure">
                      <CompanyStructure
                        companyStructure={applyingCompany.groupStructure}
                      />
                    </DetailsCard>
                  </div>
                )}

                {applyingCompany.beneficialOwners &&
                  applyingCompany.beneficialOwners.length > 0 && (
                    <div className="mb-5 w-full px-3">
                      <DetailsCard title="Beneficial owners">
                        <Table>
                          <thead className="bg-gray-100 [&_tr]:border-b [&_tr]:border-gray-300">
                            <tr>
                              <th className="h-10 px-2 text-left align-middle text-table-header">
                                Name
                              </th>
                              <th className="h-10 px-2 text-left align-middle text-table-header">
                                Person Number
                              </th>
                              <th className="h-10 px-2 text-left align-middle text-table-header">
                                Extent of Control
                              </th>
                              <th className="h-10 px-2 text-left align-middle text-table-header">
                                PEP
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {applyingCompany.beneficialOwners.map(
                              (beneficialOwner, index) => (
                                <tr
                                  key={index}
                                  className="border-b transition-colors hover:bg-gray-100/50 even:bg-gray-100"
                                >
                                  <td className="p-2 align-middle text-table-cell">
                                    {beneficialOwner.fullName}
                                  </td>
                                  <td className="p-2 align-middle text-table-cell">
                                    {beneficialOwner.ssn}
                                  </td>
                                  <td className="p-2 align-middle text-table-cell">
                                    {beneficialOwner.extentOfControlDescription}
                                  </td>
                                  <td className="p-2 align-middle text-table-cell">
                                    {getIsPepTextByCondition(
                                      beneficialOwner.isPep,
                                    )}
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </Table>
                      </DetailsCard>
                    </div>
                  )}

                <div className="mb-5 w-full px-3 xl:w-1/2">
                  <DetailsCard title="Loan request details">
                    <DetailsItemGrid>
                      <DetailsItemGridData
                        title="Amount"
                        value={formattedCurrencyValue(
                          loanApplication.requestedLoanAmount,
                        )}
                      />

                      <DetailsItemGridData
                        title="Repayment time"
                        value={`${loanApplication.requestedPaybackTimeMonths} ${
                          loanApplication.requestedPaybackTimeMonths > 1
                            ? "months"
                            : "month"
                        }`}
                      />

                      <DetailsItemGridData title="Reason" value={reasonText} />
                    </DetailsItemGrid>
                  </DetailsCard>
                </div>
                <div className="mb-5 w-full px-3 xl:w-1/2">
                  <DetailsCard title="KYC">
                    <DetailsItemGrid>
                      <DetailsItemGridData
                        indicator
                        isDone={
                          contactPerson.isPep != null && !contactPerson.isPep
                        }
                        notExists={contactPerson.isPep == null}
                        title="PEP"
                        value={getIsPepTextByCondition(contactPerson.isPep)}
                        tooltip={getIsPepTextByCondition(contactPerson.isPep)}
                      />
                      <DetailsItemGridData
                        indicator
                        isDone={
                          loanApplication.contactPersonHasSigningRightForCompany !=
                            null &&
                          loanApplication.contactPersonHasSigningRightForCompany
                        }
                        notExists={
                          loanApplication.contactPersonHasSigningRightForCompany ==
                          null
                        }
                        title="Signatories"
                        value={signatoriesText}
                        tooltip={signatoriesText}
                      />

                      {customerCompany.type !== "SOLE_TRADER" && (
                        <DetailsItemGridData
                          indicator
                          title="Beneficial Owner"
                          isDone={!!applyingCompany.beneficialOwnerExists}
                          notExists={
                            applyingCompany.beneficialOwnerExists == null
                          }
                          value={beneficialOwnerText}
                          tooltip={beneficialOwnerText}
                        />
                      )}
                    </DetailsItemGrid>
                  </DetailsCard>
                </div>

                {fieldRequestObject?.leasing?.isSubmitted && (
                  <div className="mb-5 w-full px-3">
                    <DetailsCard title="Leasing">
                      {!fieldRequestObject.leasing.data ? (
                        <DetailsItemGrid columns={4}>
                          <DetailsItemGridData
                            title="Leasing object"
                            notExists
                          />
                        </DetailsItemGrid>
                      ) : (
                        <DetailsItemGrid columns={4}>
                          {fieldRequestObject.leasing.data?.leasingAmount && (
                            <DetailsItemGridData
                              title="Leasing amount"
                              value={formattedCurrencyValue(
                                fieldRequestObject.leasing.data?.leasingAmount,
                              )}
                            />
                          )}

                          <DetailsItemGridData
                            title="Leasing object"
                            value={fieldRequestObject.leasing.data?.leasingProductType?.replace(
                              /_/g,
                              " ",
                            )}
                          />

                          {fieldRequestObject.leasing_additional_data?.data
                            ?.leasingProductDescription && (
                            <DetailsItemGridData
                              title="Description"
                              value={
                                <>
                                  <FreeText
                                    text={
                                      fieldRequestObject.leasing_additional_data
                                        .data.leasingProductDescription
                                    }
                                  />
                                </>
                              }
                            />
                          )}

                          {fieldRequestObject.leasing_additional_data?.data
                            ?.files &&
                            !isEmpty(
                              fieldRequestObject.leasing_additional_data.data
                                .files,
                            ) &&
                            fieldRequestObject.leasing_additional_data.data
                              .files.length > 0 && (
                              <DetailsItemGridData
                                title="Files"
                                colSpan="full"
                                value={
                                  <DetailsItemList>
                                    {fieldRequestObject.leasing_additional_data.data.files.map(
                                      (item) => (
                                        <li key={item.fileId}>
                                          <AttachedFile
                                            title={item.originalFileName ?? ""}
                                            url={item.url}
                                          />
                                        </li>
                                      ),
                                    )}
                                  </DetailsItemList>
                                }
                              />
                            )}

                          {fieldRequestObject.leasing_additional_data?.data
                            ?.leasingProductUrl && (
                            <DetailsItemGridData
                              title="URL link to the object"
                              value={
                                <AttachedLink
                                  value={
                                    fieldRequestObject.leasing_additional_data
                                      .data.leasingProductUrl
                                  }
                                />
                              }
                            />
                          )}
                        </DetailsItemGrid>
                      )}
                    </DetailsCard>
                  </div>
                )}

                <CreditCaseFileBlock
                  creditCaseFileUrl={
                    applyingCompany.creditCaseFile?.fileUrl ?? null
                  }
                  creditCaseFileTitle={
                    applyingCompany.creditCaseFile?.originalFileName ?? null
                  }
                />
                {loanApplication.purposeFreeTextAnswer && (
                  <div className="mb-5 w-full px-3">
                    <DetailsCard title="Information about the company and purpose of loan request">
                      <DetailsItemList>
                        <li className="text-base">
                          {loanApplication.purposeFreeTextAnswer}
                        </li>
                      </DetailsItemList>
                    </DetailsCard>
                  </div>
                )}
                {fieldRequestObject?.refinancing_amounts?.isSubmitted && (
                  <div className="mb-5 w-full px-3">
                    <DetailsCard title="Existing financing">
                      {!fieldRequestObject.refinancing_amounts.data ? (
                        <div className="text-base-secondary">
                          No information
                        </div>
                      ) : (
                        <>
                          <Table>
                            <thead className="bg-gray-100 [&_tr]:border-b [&_tr]:border-gray-300">
                              <tr>
                                <th className="h-10 px-2 text-left align-middle text-table-header">
                                  Lender Name
                                </th>
                                <th className="h-10 px-2 text-left align-middle text-table-header">
                                  Outstanding Amount
                                </th>
                                <th className="h-10 px-2 text-left align-middle text-table-header">
                                  Monthly Cost
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {fieldRequestObject.refinancing_amounts.data.refinancingAmounts.map(
                                (item, index) => (
                                  <tr
                                    key={`${index}refinancing_amounts`}
                                    className="border-b transition-colors hover:bg-gray-100/50 even:bg-gray-100"
                                  >
                                    <td className="p-2 align-middle text-table-cell">
                                      {item.lenderName}
                                    </td>
                                    <td className="p-2 align-middle text-table-cell">
                                      {item.outstandingAmount}
                                    </td>
                                    <td className="p-2 align-middle text-table-cell">
                                      {item.monthlyCost}
                                    </td>
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </Table>
                          {!isEmpty(
                            fieldRequestObject.refinancing_amounts.data.files,
                          ) && (
                            <div className="mt-4">
                              <TextItem
                                text="Uploaded files"
                                className="mb-2"
                              />
                              <DetailsItemList>
                                {fieldRequestObject.refinancing_amounts.data.files.map(
                                  (item) => (
                                    <li key={item.fileId}>
                                      <AttachedFile
                                        title={item.originalFileName ?? ""}
                                        url={item.url}
                                      />
                                    </li>
                                  ),
                                )}
                              </DetailsItemList>
                            </div>
                          )}
                        </>
                      )}
                    </DetailsCard>
                  </div>
                )}
                {applyingCompany.boardMembers &&
                  applyingCompany.boardMembers.length > 0 && (
                    <div className="mb-5 w-full px-3">
                      <DetailsCard title="Board members">
                        <Table>
                          <thead className="bg-gray-100 [&_tr]:border-b [&_tr]:border-gray-300">
                            <tr>
                              <th className="h-10 px-2 text-left align-middle text-table-header">
                                Name
                              </th>
                              <th className="h-10 px-2 text-left align-middle text-table-header">
                                Role
                              </th>
                              <th className="h-10 px-2 text-left align-middle text-table-header">
                                Person Number
                              </th>
                              <th className="h-10 px-2 text-left align-middle text-table-header">
                                Joined at
                              </th>
                              {/*
                        TODO : Turned off for now, See KREA-2699
                        <th>Background check</th>
                       */}
                            </tr>
                          </thead>
                          <tbody>
                            {applyingCompany.boardMembers.map((item, index) => (
                              <tr
                                key={index}
                                className="border-b transition-colors hover:bg-gray-100/50 even:bg-gray-100"
                              >
                                <td className="p-2 align-middle text-table-cell">
                                  {!item.firstName && !item.lastName
                                    ? "No name registered"
                                    : `${item.firstName ? item.firstName : ""} ${item.lastName ? item.lastName : ""}`}
                                </td>
                                <td className="p-2 align-middle text-table-cell">
                                  {item.roles && item.roles.length > 0
                                    ? item.roles.map((itm, indx) => (
                                        <div
                                          key={indx}
                                        >{`${itm.roleName} (${itm.roleCode})`}</div>
                                      ))
                                    : "-"}
                                </td>
                                <td className="p-2 align-middle text-table-cell">
                                  {item.ssn}
                                </td>
                                <td className="p-2 align-middle text-table-cell">
                                  {item.roles && item.roles.length > 0
                                    ? item.roles.map((itm, indx) => (
                                        <div key={indx}>{itm.fromDate}</div>
                                      ))
                                    : "-"}
                                </td>
                                {/* TODO : Turned off for now, See KREA-2699 */}
                                {/* <td>{personBackgroundText(item.ssn)}</td> */}
                                {/* TODO : Turned off for now, See KREA-2699 */}
                                {/* <td className="text-right">
                          {getPersonBackground(item.ssn) !== null && (
                            <div className="d-inline-block">
                              <DataStatusIndicator
                                isDone={getPersonBackground(item.ssn) === 'OK'}
                                tooltip={personBackgroundText(item.ssn)}
                              />
                            </div>
                          )}
                        </td> */}
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </DetailsCard>
                    </div>
                  )}

                <div
                  id="section-financial"
                  className="mb-5 w-full px-3"
                  style={{ scrollMarginTop: 80 }}
                >
                  <RepaymentCapacityTab
                    hideNav
                    scrollOffset={80}
                    selfEstimatedRevenueValue={
                      fieldRequestObject?.self_estimated_revenue?.data
                        ?.selfEstimatedRevenue ?? null
                    }
                    selfEstimatedHistoricRevenueValue={
                      fieldRequestObject?.self_estimated_historic_revenue?.data
                        ?.selfEstimatedHistoricRevenue ?? null
                    }
                    accountingDataFiles={
                      fieldRequestObject?.accounting_data?.data?.files?.map(
                        (f) => ({
                          id: String(f.fileId),
                          name: f.originalFileName ?? "",
                          url: f.url,
                        }),
                      ) ?? null
                    }
                    bankingDataFiles={
                      fieldRequestObject?.banking_data?.data?.files?.map(
                        (f) => ({
                          id: String(f.fileId),
                          name: f.originalFileName ?? "",
                          url: f.url,
                        }),
                      ) ?? null
                    }
                    annualReportFiles={
                      fieldRequestObject?.annual_report?.data?.files?.map(
                        (f) => ({
                          id: String(f.fileId),
                          name: f.originalFileName ?? "",
                          url: f.url,
                        }),
                      ) ?? null
                    }
                    insightsRevenueLast12Months={
                      insights?.revenueLast12Months ?? null
                    }
                    insightsRevenueSinceFinancialReport={
                      insights?.revenueSinceFinancialReport ?? null
                    }
                    accountingData={accountingData}
                    currency={loanApplication.currency}
                    countryCode={loanApplication.countryCode}
                    creditScoreColor={applyingCompany.creditScoreColor}
                    creditScoreStatus={applyingCompany.creditScoreStatus}
                  />
                </div>

                <div className="mb-5 w-full px-3">
                  <DetailsCard
                    title={
                      <>
                        Collateral
                        {latestFinancialRecord?.financialYearEnd && (
                          <span className="ml-1 text-label font-normal">
                            (as of{" "}
                            {dayjs(
                              latestFinancialRecord.financialYearEnd,
                            ).format("YYYY-MM-DD")}
                            )
                          </span>
                        )}
                      </>
                    }
                  >
                    <DetailsItemGrid columns={4}>
                      <DetailsItemGridData
                        complex
                        colSpan="full"
                        title="Guarantors"
                        value={
                          fieldRequestObject?.guarantors?.isSubmitted &&
                          !fieldRequestObject.guarantors.data ? (
                            <span className="font-semibold text-base-secondary">
                              No information
                            </span>
                          ) : fieldRequestObject?.guarantors?.isSubmitted ? (
                            <DetailsItemList className="divide-y divide-gray-200 rounded-xl border border-gray-200 bg-white px-4">
                              {fieldRequestObject.guarantors.data?.guarantors.map(
                                (guarantor, index) => (
                                  <li
                                    key={index}
                                    // mb-0 overrides DetailsItemList's blanket
                                    // [&_li]:mb-3, which would break the even
                                    // spacing the divider needs.
                                    className={cn("mb-0! py-4")}
                                  >
                                    <div>
                                      <span className="font-semibold">
                                        {`${guarantor.ssn} ${guarantor.firstName ? guarantor.firstName : ""} ${
                                          guarantor.lastName
                                            ? guarantor.lastName
                                            : ""
                                        }`}
                                      </span>
                                      {` ${guarantor.email ? `- ${guarantor.email}` : ""}`}
                                      {` ${guarantor.mobilePhoneNumber ? `- ${guarantor.mobilePhoneNumber}` : ""}`}
                                    </div>
                                    {(() => {
                                      const taxDocuments =
                                        guarantor.taxDocuments ?? [];
                                      if (taxDocuments.length === 0)
                                        return null;

                                      return (
                                        // Same presentation as the accounting
                                        // data files in RepaymentCapacityTab's
                                        // FilesSection: a FileSection card
                                        // (primary-150) with Eye / Download
                                        // icon actions.
                                        <div className="mt-2 flex flex-col gap-2">
                                          <TextItem text="Tax documents" />
                                          {taxDocuments.map((taxDocument) => (
                                            <FileSection
                                              key={taxDocument.fileId}
                                              title={
                                                taxDocument.originalFileName ??
                                                "Tax document"
                                              }
                                              actions={
                                                <div className="flex items-center">
                                                  <FileSectionAction
                                                    title="View file"
                                                    Icon={Eye}
                                                    onClick={() => {
                                                      const url = new URL(
                                                        taxDocument.url,
                                                      );
                                                      url.searchParams.set(
                                                        "download",
                                                        "false",
                                                      );
                                                      window.open(
                                                        url.toString(),
                                                        "_blank",
                                                        "noopener,noreferrer",
                                                      );
                                                    }}
                                                  />
                                                  <FileSectionAction
                                                    title="Download file"
                                                    Icon={Download}
                                                    onClick={() =>
                                                      downloadFile(
                                                        taxDocument.url,
                                                        taxDocument.originalFileName,
                                                      ).catch(() => {
                                                        toast.error(
                                                          "Failed to download file.",
                                                        );
                                                      })
                                                    }
                                                  />
                                                </div>
                                              }
                                            />
                                          ))}
                                        </div>
                                      );
                                    })()}
                                    {guarantor.bankingData
                                      ?.estimatedMonthlyIncome ? (
                                      <ResponsiveTooltip
                                        trigger={
                                          <div className="mb-2 inline-block">
                                            Estimated monthly income:{" "}
                                            {formattedCurrencyValue(
                                              guarantor.bankingData
                                                .estimatedMonthlyIncome,
                                            )}
                                          </div>
                                        }
                                      >
                                        Only for full months
                                      </ResponsiveTooltip>
                                    ) : null}
                                    <div className="mb-2">
                                      <BankingDataChart
                                        resourceId={Number(guarantor.id)}
                                        resourceType={"GUARANTOR"}
                                        primaryCurrency={
                                          loanApplication.currency
                                        }
                                        isMobile={isMobile}
                                      />
                                    </div>
                                  </li>
                                ),
                              )}
                            </DetailsItemList>
                          ) : (
                            <span className="font-semibold text-red-500">
                              No guarantors
                            </span>
                          )
                        }
                      />

                      {latestFinancialRecord && (
                        <DetailsItemGridData
                          complex
                          colSpan="full"
                          title="Assets"
                          value={
                            <div className="flex flex-col xl:grid xl:grid-cols-4 gap-4">
                              {(latestFinancialRecord.tradeReceivables ?? 0) >
                                0 && (
                                <div className="flex flex-col gap-1">
                                  <span className="text-label">
                                    Trade receivables
                                  </span>
                                  <span className="text-base-strong">
                                    {formattedCurrencyValue(
                                      latestFinancialRecord.tradeReceivables,
                                    )}
                                  </span>
                                </div>
                              )}

                              {(latestFinancialRecord.otherCurrentAssets ?? 0) >
                                0 && (
                                <div className="flex flex-col gap-1">
                                  <span className="text-label">
                                    Other current assets
                                  </span>
                                  <span className="text-base-strong">
                                    {formattedCurrencyValue(
                                      latestFinancialRecord.otherCurrentAssets,
                                    )}
                                  </span>
                                </div>
                              )}

                              {(latestFinancialRecord.inventories ?? 0) > 0 && (
                                <div className="flex flex-col gap-1">
                                  <span className="text-label">
                                    Inventories
                                  </span>
                                  <span className="text-base-strong">
                                    {formattedCurrencyValue(
                                      latestFinancialRecord.inventories,
                                    )}
                                  </span>
                                </div>
                              )}

                              {(latestFinancialRecord.equipmentAndMachines ??
                                0) > 0 && (
                                <div className="flex flex-col gap-1">
                                  <span className="text-label">
                                    Equipment &amp; machines
                                  </span>
                                  <span className="text-base-strong">
                                    {formattedCurrencyValue(
                                      latestFinancialRecord.equipmentAndMachines,
                                    )}
                                  </span>
                                </div>
                              )}
                              {(latestFinancialRecord.landAndBuilding ?? 0) >
                                0 && (
                                <div className="flex flex-col gap-1">
                                  <span className="text-label">
                                    Land &amp; building
                                  </span>
                                  <span className="text-base-strong">
                                    {formattedCurrencyValue(
                                      latestFinancialRecord.landAndBuilding,
                                    )}
                                  </span>
                                </div>
                              )}
                              {(latestFinancialRecord.financialAssets ?? 0) >
                                0 && (
                                <div className="flex flex-col gap-1">
                                  <span className="text-label">
                                    Financial assets
                                  </span>
                                  <span className="text-base-strong">
                                    {formattedCurrencyValue(
                                      latestFinancialRecord.financialAssets,
                                    )}
                                  </span>
                                </div>
                              )}
                            </div>
                          }
                        />
                      )}
                    </DetailsItemGrid>
                  </DetailsCard>
                </div>

                {(fieldRequestObject?.additional_securities?.isSubmitted ||
                  fieldRequestObject?.budget?.isSubmitted) && (
                  <div className="mb-5 w-full px-3">
                    <DetailsCard title="Other information">
                      <DetailsItemGrid columns={4}>
                        {fieldRequestObject.additional_securities
                          ?.isSubmitted &&
                          !fieldRequestObject.additional_securities.data && (
                            <DetailsItemGridData
                              title="Additional securities"
                              colSpan="full"
                              notExists
                            />
                          )}

                        {fieldRequestObject.additional_securities?.data
                          ?.additionalSecuritiesText && (
                          <DetailsItemGridData
                            notExists={
                              !fieldRequestObject.additional_securities.data
                                .additionalSecuritiesText
                            }
                            title="Comment"
                            colSpan="full"
                            value={
                              <FreeText
                                text={
                                  fieldRequestObject.additional_securities.data
                                    .additionalSecuritiesText
                                }
                              />
                            }
                          />
                        )}

                        {!isEmpty(
                          fieldRequestObject.additional_securities?.data?.files,
                        ) && (
                          <DetailsItemGridData
                            title="Uploaded files"
                            colSpan="full"
                            value={
                              <DetailsItemList className="-mt-1 mb-0">
                                {fieldRequestObject.additional_securities?.data?.files.map(
                                  (item) => (
                                    <li key={item.fileId}>
                                      <AttachedFile
                                        title={item.originalFileName ?? ""}
                                        url={item.url}
                                      />
                                    </li>
                                  ),
                                )}
                              </DetailsItemList>
                            }
                          />
                        )}

                        {fieldRequestObject.budget?.isSubmitted && (
                          <DetailsItemGridData
                            title="Budget"
                            colSpan="full"
                            notExists={isEmpty(
                              fieldRequestObject.budget.data?.files,
                            )}
                            value={
                              fieldRequestObject.budget.data && (
                                <DetailsItemList>
                                  {!isEmpty(
                                    fieldRequestObject.budget.data.files,
                                  ) &&
                                    fieldRequestObject.budget.data.files.map(
                                      (item) => (
                                        <li key={item.fileId}>
                                          <AttachedFile
                                            title={item.originalFileName ?? ""}
                                            url={item.url}
                                          />
                                        </li>
                                      ),
                                    )}
                                </DetailsItemList>
                              )
                            }
                          />
                        )}
                      </DetailsItemGrid>
                    </DetailsCard>
                  </div>
                )}

                {!!latestFinancialRecord && (
                  <div className="mb-5 w-full px-3">
                    <DetailsCard
                      title={
                        <>
                          Company mortgage as collateral
                          {latestFinancialRecord.financialYearEnd && (
                            <span className="ml-1 text-label font-normal">
                              (as of{" "}
                              {dayjs(
                                latestFinancialRecord.financialYearEnd,
                              ).format("YYYY-MM-DD")}
                              )
                            </span>
                          )}
                        </>
                      }
                    >
                      <DetailsItemGrid className="mt-5" columns={4}>
                        {latestFinancialRecord.tradeReceivables !== null && (
                          <DetailsItemGridData
                            title="Trade receivables"
                            value={formattedCurrencyValue(
                              latestFinancialRecord.tradeReceivables,
                            )}
                          />
                        )}
                      </DetailsItemGrid>
                    </DetailsCard>
                  </div>
                )}

                {(fieldRequestObject?.property?.data?.propertyUnitDesignation ||
                  fieldRequestObject?.property_additional_data?.data
                    ?.propertyValuation ||
                  fieldRequestObject?.parent_company_guarantee?.isSubmitted ||
                  fieldRequestObject?.share_mortgage?.isSubmitted ||
                  fieldRequestObject?.building_permit?.isSubmitted ||
                  fieldRequestObject?.construction_estimate?.isSubmitted) && (
                  <div className="mb-5 w-full px-3">
                    <DetailsCard title="Property as collateral">
                      <DetailsItemGrid columns={4}>
                        <DetailsItemGridData
                          complex
                          colSpan="full"
                          title="Real estate mortgage"
                          value={
                            <ul className="divide-y">
                              {fieldRequestObject.property?.data
                                ?.propertyUnitDesignation && (
                                <li className="py-2">
                                  <TextItem
                                    text="Property unit designation"
                                    value={
                                      fieldRequestObject.property.data
                                        .propertyUnitDesignation
                                    }
                                  />
                                </li>
                              )}

                              {fieldRequestObject.property_additional_data?.data
                                ?.propertyValuation && (
                                <li className="py-2">
                                  <TextItem
                                    text="Property valuation"
                                    value={formattedCurrencyValue(
                                      fieldRequestObject
                                        .property_additional_data.data
                                        .propertyValuation,
                                    )}
                                  />
                                </li>
                              )}

                              {fieldRequestObject.property_additional_data?.data
                                ?.propertyUrl && (
                                <li className="py-2">
                                  <TextItem
                                    text="Property URL"
                                    value={
                                      <AttachedLink
                                        value={
                                          fieldRequestObject
                                            .property_additional_data.data
                                            .propertyUrl
                                        }
                                      />
                                    }
                                  />
                                </li>
                              )}

                              {!isEmpty(
                                fieldRequestObject.property_additional_data
                                  ?.data?.files,
                              ) && (
                                <li className="py-2">
                                  <span className="text-label">
                                    Uploaded files
                                  </span>
                                  <DetailsItemList className="mt-1">
                                    {fieldRequestObject.property_additional_data?.data?.files.map(
                                      (item) => (
                                        <li key={item.fileId}>
                                          <AttachedFile
                                            title={item.originalFileName ?? ""}
                                            url={item.url}
                                          />
                                        </li>
                                      ),
                                    )}
                                  </DetailsItemList>
                                </li>
                              )}
                            </ul>
                          }
                        />

                        {(fieldRequestObject.parent_company_guarantee
                          ?.isSubmitted ||
                          fieldRequestObject.share_mortgage?.isSubmitted ||
                          fieldRequestObject.building_permit?.isSubmitted ||
                          fieldRequestObject.construction_estimate
                            ?.isSubmitted) && (
                          <DetailsItemGridData
                            complex
                            colSpan="full"
                            title="Supporting collateral"
                            value={
                              <ul className="divide-y">
                                {fieldRequestObject.parent_company_guarantee
                                  ?.isSubmitted && (
                                  <li className="py-2">
                                    <span className="text-base-strong">
                                      Parent company guarantee
                                    </span>
                                    {!isEmpty(
                                      fieldRequestObject
                                        .parent_company_guarantee.data?.files,
                                    ) ? (
                                      <div className="mt-2">
                                        <span className="text-label">
                                          Uploaded files
                                        </span>
                                        <DetailsItemList className="mt-1">
                                          {fieldRequestObject.parent_company_guarantee.data?.files.map(
                                            (item) => (
                                              <li key={item.fileId}>
                                                <AttachedFile
                                                  title={
                                                    item.originalFileName ?? ""
                                                  }
                                                  url={item.url}
                                                />
                                              </li>
                                            ),
                                          )}
                                        </DetailsItemList>
                                      </div>
                                    ) : (
                                      <div className="text-base-secondary">
                                        No information
                                      </div>
                                    )}
                                  </li>
                                )}

                                {fieldRequestObject.share_mortgage
                                  ?.isSubmitted && (
                                  <li className="py-2">
                                    <span className="text-base-strong">
                                      Share mortgage
                                    </span>
                                    {fieldRequestObject.share_mortgage.data
                                      ?.shareMortgageText ? (
                                      <div className="mt-2">
                                        <TextItem
                                          text="Free text by customer"
                                          value={
                                            fieldRequestObject.share_mortgage
                                              .data?.shareMortgageText
                                          }
                                        />
                                      </div>
                                    ) : (
                                      <div className="text-base-secondary">
                                        No information
                                      </div>
                                    )}
                                  </li>
                                )}

                                {fieldRequestObject.building_permit
                                  ?.isSubmitted && (
                                  <li className="py-2">
                                    <span className="text-base-strong">
                                      Building permit
                                    </span>
                                    {!isEmpty(
                                      fieldRequestObject.building_permit.data
                                        ?.files,
                                    ) ? (
                                      <div className="mt-2">
                                        <span className="text-label">
                                          Uploaded files
                                        </span>
                                        <DetailsItemList className="mt-1">
                                          {fieldRequestObject.building_permit.data?.files.map(
                                            (item) => (
                                              <li key={item.fileId}>
                                                <AttachedFile
                                                  title={
                                                    item.originalFileName ?? ""
                                                  }
                                                  url={item.url}
                                                />
                                              </li>
                                            ),
                                          )}
                                        </DetailsItemList>
                                      </div>
                                    ) : (
                                      <div className="text-base-secondary">
                                        No information
                                      </div>
                                    )}
                                  </li>
                                )}

                                {fieldRequestObject.construction_estimate
                                  ?.isSubmitted && (
                                  <li className="py-2">
                                    <span className="text-base-strong">
                                      Construction estimate
                                    </span>
                                    {!isEmpty(
                                      fieldRequestObject.construction_estimate
                                        .data?.files,
                                    ) ? (
                                      <div className="mt-2">
                                        <span className="text-label">
                                          Uploaded files
                                        </span>
                                        <DetailsItemList className="mt-1">
                                          {fieldRequestObject.construction_estimate.data?.files.map(
                                            (item) => (
                                              <li key={item.fileId}>
                                                <AttachedFile
                                                  title={
                                                    item.originalFileName ?? ""
                                                  }
                                                  url={item.url}
                                                />
                                              </li>
                                            ),
                                          )}
                                        </DetailsItemList>
                                      </div>
                                    ) : (
                                      <div className="text-base-secondary">
                                        No information
                                      </div>
                                    )}
                                  </li>
                                )}
                              </ul>
                            }
                          />
                        )}
                      </DetailsItemGrid>
                    </DetailsCard>
                  </div>
                )}

                {loanApplication.eligibleForInvoicePurchaseBids && (
                  <div className="mb-5 w-full px-3">
                    <DetailsCard title="Invoices as collateral">
                      <DetailsItemGrid columns={4}>
                        <DetailsItemGridData
                          title="Monthly amount"
                          value={formattedCurrencyValue(
                            applyingCompany.estimatedInvoiceAmount,
                          )}
                        />

                        <DetailsItemGridData
                          title="Number of invoices"
                          value={applyingCompany.numberOfInvoices}
                        />

                        <DetailsItemGridData title="Type" value={"Invoices"} />

                        <DetailsItemGridData
                          title="Average due days"
                          value={applyingCompany.averagePaymentTimeDays}
                        />

                        {applyingCompany.invoicesDescription && (
                          <DetailsItemGridData
                            colSpan="full"
                            title="Description"
                            value={
                              <div className="font-normal">
                                <FreeText
                                  text={applyingCompany.invoicesDescription}
                                />
                              </div>
                            }
                          />
                        )}

                        {!isEmpty(
                          fieldRequestObject?.unpaid_invoices_additional_data
                            ?.data?.files,
                        ) && (
                          <DetailsItemGridData
                            title="Uploaded files"
                            colSpan="full"
                            value={
                              <DetailsItemList>
                                {!isEmpty(
                                  fieldRequestObject
                                    ?.unpaid_invoices_additional_data?.data
                                    ?.files,
                                ) &&
                                  fieldRequestObject?.unpaid_invoices_additional_data?.data?.files.map(
                                    (item) => (
                                      <li key={item.fileId}>
                                        <AttachedFile
                                          title={item.originalFileName ?? ""}
                                          url={item.url}
                                        />
                                      </li>
                                    ),
                                  )}
                              </DetailsItemList>
                            }
                          />
                        )}
                      </DetailsItemGrid>
                    </DetailsCard>
                  </div>
                )}

                {loanApplication.eligibleForRealEstateBids && (
                  <div className="mb-5 w-full px-3">
                    <DetailsCard title="Repayment of loan (real estate applications only)">
                      <DetailsItemList>
                        <li>
                          <TextItem
                            text="By refinancing the loan"
                            value={
                              realEstateRepaymentChoicesObject.refinancing
                                ?.selected ||
                              fieldRequestObject?.refinancing?.isSubmitted
                                ? "Yes"
                                : ""
                            }
                            notExists={
                              !realEstateRepaymentChoicesObject.refinancing
                                ?.selected &&
                              !fieldRequestObject?.refinancing?.isSubmitted
                            }
                          />
                          {fieldRequestObject?.refinancing?.isSubmitted && (
                            <DetailsItemList className="mt-2">
                              {fieldRequestObject.refinancing.data
                                ?.refinancingText && (
                                <li>
                                  <FreeText
                                    title="Free text by customer"
                                    text={
                                      fieldRequestObject.refinancing.data
                                        ?.refinancingText
                                    }
                                  />
                                </li>
                              )}
                            </DetailsItemList>
                          )}
                        </li>
                        <li>
                          <TextItem
                            text="By sale of property"
                            value={
                              realEstateRepaymentChoicesObject.sale_of_property
                                ?.selected ||
                              fieldRequestObject?.sale_of_property?.isSubmitted
                                ? "Yes"
                                : ""
                            }
                            notExists={
                              !realEstateRepaymentChoicesObject.sale_of_property
                                ?.selected &&
                              !fieldRequestObject?.sale_of_property?.isSubmitted
                            }
                          />
                          {fieldRequestObject?.sale_of_property
                            ?.isSubmitted && (
                            <DetailsItemList className="mt-2">
                              {fieldRequestObject.sale_of_property.data
                                ?.saleOfPropertyText && (
                                <li>
                                  <FreeText
                                    title="Free text by customer"
                                    text={
                                      fieldRequestObject.sale_of_property.data
                                        ?.saleOfPropertyText
                                    }
                                  />
                                </li>
                              )}
                            </DetailsItemList>
                          )}
                        </li>
                        <li>
                          <TextItem
                            text="By cashflow from the property"
                            value={
                              realEstateRepaymentChoicesObject.property_revenue
                                ?.selected ||
                              fieldRequestObject?.property_cash_flow
                                ?.isSubmitted
                                ? "Yes"
                                : ""
                            }
                            notExists={
                              !realEstateRepaymentChoicesObject.property_revenue
                                ?.selected &&
                              !fieldRequestObject?.property_cash_flow
                                ?.isSubmitted
                            }
                          />
                          {fieldRequestObject?.property_cash_flow
                            ?.isSubmitted && (
                            <DetailsItemList className="mt-2">
                              <li>
                                <TextItem
                                  text="Uploaded files"
                                  notExists={isEmpty(
                                    fieldRequestObject.property_cash_flow.data
                                      ?.files,
                                  )}
                                  className="mb-2"
                                />
                                <DetailsItemList>
                                  {!isEmpty(
                                    fieldRequestObject.property_cash_flow.data
                                      ?.files,
                                  ) &&
                                    fieldRequestObject.property_cash_flow.data?.files.map(
                                      (item) => (
                                        <li key={item.fileId}>
                                          <AttachedFile
                                            title={item.originalFileName ?? ""}
                                            url={item.url}
                                          />
                                        </li>
                                      ),
                                    )}
                                </DetailsItemList>
                              </li>
                            </DetailsItemList>
                          )}
                        </li>
                        {isBoolean(
                          realEstateRepaymentChoicesObject.company_revenue
                            ?.selected,
                        ) && (
                          <li>
                            <TextItem
                              text="By cashflow from the company"
                              value={
                                realEstateRepaymentChoicesObject.company_revenue
                                  .selected
                                  ? "Yes"
                                  : ""
                              }
                              notExists={
                                !realEstateRepaymentChoicesObject
                                  .company_revenue.selected
                              }
                            />
                            {realEstateRepaymentChoicesObject.company_revenue
                              .selected && (
                              <DetailsItemList className="mt-2">
                                {fieldRequestObject?.accounting_data?.data &&
                                  !isEmpty(
                                    fieldRequestObject.accounting_data.data
                                      .files,
                                  ) && (
                                    <li>
                                      <TextItem
                                        text="Uploaded files"
                                        notExists={isEmpty(
                                          fieldRequestObject.accounting_data
                                            .data.files,
                                        )}
                                        className="mb-2"
                                      />

                                      <DetailsItemList>
                                        {fieldRequestObject.accounting_data.data.files.map(
                                          (item) => (
                                            <li key={item.fileId}>
                                              <AttachedFile
                                                title={
                                                  item.originalFileName ?? ""
                                                }
                                                url={item.url}
                                              />
                                            </li>
                                          ),
                                        )}
                                      </DetailsItemList>
                                    </li>
                                  )}
                              </DetailsItemList>
                            )}
                          </li>
                        )}
                        {(realEstateRepaymentChoicesObject
                          .construction_completed?.selected ||
                          fieldRequestObject?.construction_completed_repayment
                            ?.isSubmitted) && (
                          <li>
                            <TextItem
                              text="Repayment after the construction is complete"
                              value={
                                realEstateRepaymentChoicesObject
                                  .construction_completed?.selected ||
                                fieldRequestObject
                                  ?.construction_completed_repayment
                                  ?.isSubmitted
                                  ? "Yes"
                                  : ""
                              }
                              notExists={
                                !realEstateRepaymentChoicesObject
                                  .construction_completed?.selected &&
                                !fieldRequestObject
                                  ?.construction_completed_repayment
                                  ?.isSubmitted
                              }
                            />
                            {fieldRequestObject
                              ?.construction_completed_repayment
                              ?.isSubmitted && (
                              <DetailsItemList className="mt-2">
                                {fieldRequestObject
                                  .construction_completed_repayment.data
                                  ?.constructionCompletedRepaymentText && (
                                  <li>
                                    {`Free text by customer: ${fieldRequestObject.construction_completed_repayment.data?.constructionCompletedRepaymentText} `}
                                  </li>
                                )}
                              </DetailsItemList>
                            )}
                          </li>
                        )}
                      </DetailsItemList>
                    </DetailsCard>
                  </div>
                )}
              </div>
            </DetailsContainer>
            <div id="section-offer" style={{ scrollMarginTop: 80 }}>
              {lenderApplication.type !==
              LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_BID_SELECTED ? (
                <Element name="scrollToNotes">
                  <LenderNotes
                    lenderInternalNotes={
                      lenderApplication.lenderInternalComments
                    }
                    lenderInternalCommentsUpdatedAt={
                      lenderApplication.lenderInternalCommentsUpdatedAt
                    }
                    lenderApplicationId={lenderApplication.id}
                  />
                </Element>
              ) : null}
              {!isEmpty(lenderResponsesWithLoanBids) && (
                <Bids
                  lenderResponses={lenderResponsesWithLoanBids ?? []}
                  title="Your loan offers"
                  lenderApplication={lenderApplication}
                />
              )}
              {!isEmpty(lenderResponsesWithFactoringBids) && (
                <Bids
                  lenderResponses={lenderResponsesWithFactoringBids ?? []}
                  title="Your factoring offers"
                  lenderApplication={lenderApplication}
                />
              )}
              {!isEmpty(lenderResponsesWithRealEstateBids) && (
                <Bids
                  lenderResponses={lenderResponsesWithRealEstateBids ?? []}
                  title="Your real estate offers"
                  lenderApplication={lenderApplication}
                />
              )}
              {!isEmpty(lenderResponsesWithLeasingBids) && (
                <Bids
                  lenderResponses={lenderResponsesWithLeasingBids ?? []}
                  title="Your leasing offers"
                  lenderApplication={lenderApplication}
                />
              )}
              {!isEmpty(lenderResponsesWithRevolvingCreditBids) && (
                <Bids
                  lenderResponses={lenderResponsesWithRevolvingCreditBids ?? []}
                  title="Your revolving credit offers"
                  lenderApplication={lenderApplication}
                />
              )}

              {!isEmpty(lenderResponsesWithPurchaseFinancingBids) && (
                <Bids
                  lenderResponses={
                    lenderResponsesWithPurchaseFinancingBids ?? []
                  }
                  title="Your purchase financing offers"
                  lenderApplication={lenderApplication}
                />
              )}

              {!isEmpty(lenderResponsesWithBuildingCreditBids) && (
                <Bids
                  lenderResponses={lenderResponsesWithBuildingCreditBids ?? []}
                  title="Your building credit offers"
                  lenderApplication={lenderApplication}
                />
              )}

              {!isEmpty(lenderResponsesWithMultipleBids) && (
                <Bids
                  lenderResponses={lenderResponsesWithMultipleBids ?? []}
                  title="Your combo offers"
                  lenderApplication={lenderApplication}
                />
              )}

              {![
                LenderApplicationTypeEnum.REJECTED,
                LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_PAYOUT_RECEIVED_BY_CUSTOMER,
                LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_BID_SELECTED,
                LenderApplicationTypeEnum.OTHER_LENDER_APPLICATION_PAYOUT_RECEIVED_BY_CUSTOMER,
                LenderApplicationTypeEnum.CLOSED_BY_CUSTOMER,
              ].includes(lenderApplication.type) ? (
                <>
                  {(!isEmpty(lenderResponsesWithLoanBids) ||
                    !isEmpty(lenderResponsesWithFactoringBids) ||
                    !isEmpty(lenderResponsesWithRealEstateBids) ||
                    !isEmpty(lenderResponsesWithLeasingBids) ||
                    !isEmpty(lenderResponsesWithRevolvingCreditBids)) && (
                    <div className="flex justify-center pb-5 xl:justify-start">
                      <Button variant="default" onClick={placeAnotherBid}>
                        Place another bid
                      </Button>
                    </div>
                  )}

                  <FormSwitcher lenderApplication={lenderApplication} />
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </FormProvider>
  );
};

export default LenderApplicationDetails;
